/* =============================================================================
   assets/aside.js: vanilla port of
     - app/components/Aside.tsx      AsideProvider (one open drawer at a time)
                                     + Aside (inert/aria attrs, Escape, focus
                                     trap, focus-in on open, focus restore)
     - app/components/Header.tsx     the controls that open drawers (hamburger,
                                     Search, Cart link) and useScrolled()
     - CartLineItem/CartSummary      the cart drawer's CartForm actions, run
                                     through the Ajax Cart API; the drawer is
                                     then re-rendered from Liquid through the
                                     bundled Section Rendering API.
   Loaded deferred from sections/header.liquid. Exposes window.UndepletedAside
   = {open(type), close()} for assets/predictive-search.js.
   Other scripts may dispatch `undepleted:cart-changed` on document to make the
   drawer + header count re-render; this file dispatches `undepleted:cart-updated`
   after each of its own cart mutations.
   Shared cart bus with assets/cart-main.js (the /cart page): both dispatch
   document `cart:updated` {detail: {source}} after their own changes and
   re-render on the other's, so the drawer and the page always show the same
   cart, as live's single Hydrogen cart does. This file's source is 'aside'.
   ========================================================================== */
(() => {
  'use strict';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const overlays = {};
  for (const el of document.querySelectorAll('.overlay[data-aside]')) {
    overlays[el.getAttribute('data-aside')] = el;
  }
  const header = document.querySelector('header.header');
  const toggle = header ? header.querySelector('.header-menu-mobile-toggle') : null;

  let type = 'closed';
  let cleanup = null;

  /* Aside(): expanded ? {role="dialog" aria-modal aria-labelledby} : {inert aria-hidden} */
  function paint() {
    for (const name of Object.keys(overlays)) {
      const overlay = overlays[name];
      const heading = overlay.querySelector('aside > header h3');
      if (name === type) {
        overlay.removeAttribute('inert');
        overlay.removeAttribute('aria-hidden');
        overlay.className = 'overlay expanded';
        if (heading) overlay.setAttribute('aria-labelledby', heading.id);
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('role', 'dialog');
      } else {
        overlay.setAttribute('inert', '');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.className = 'overlay ';
        overlay.removeAttribute('aria-labelledby');
        overlay.removeAttribute('aria-modal');
        overlay.removeAttribute('role');
      }
    }
    // HeaderMenuMobileToggle: aria-expanded={type === 'mobile'}
    if (toggle) toggle.setAttribute('aria-expanded', type === 'mobile' ? 'true' : 'false');
  }

  /* The useEffect in Aside() that runs while a drawer is expanded. Returns its cleanup. */
  function effect(overlay) {
    const aside = overlay.querySelector('aside');
    const abortController = new AbortController();
    const previouslyFocused = document.activeElement;
    const focusable = () => Array.from(aside ? aside.querySelectorAll(FOCUSABLE_SELECTOR) : []);

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          close();
          return;
        }
        // focus trap: Tab may never leave an open drawer
        if (event.key !== 'Tab') return;

        const items = focusable();
        const active = document.activeElement;
        const inside = aside ? aside.contains(active) : false;

        if (items.length === 0) {
          event.preventDefault();
          if (aside) aside.focus();
          return;
        }

        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey) {
          if (!inside || active === first) {
            event.preventDefault();
            last.focus();
          }
        } else if (!inside || active === last) {
          event.preventDefault();
          first.focus();
        }
      },
      {signal: abortController.signal},
    );

    // move focus into the drawer once it is visible
    const timer = window.setTimeout(() => {
      const items = focusable();
      const target = items[0] || aside;
      if (target) target.focus();
    }, 0);

    return () => {
      abortController.abort();
      window.clearTimeout(timer);
      // restore focus to whatever opened the drawer
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }

  /* AsideProvider's setType: same value = no render, no effect re-run. */
  function setType(next) {
    if (next === type) return;
    type = next;
    paint();
    if (cleanup) {
      const run = cleanup;
      cleanup = null;
      run();
    }
    if (type !== 'closed' && overlays[type]) cleanup = effect(overlays[type]);
  }

  function open(mode) {
    setType(mode);
  }

  function close() {
    setType('closed');
  }

  window.UndepletedAside = {open, close};

  /* ---- controls -------------------------------------------------------- */
  for (const overlay of Object.values(overlays)) {
    const outside = overlay.querySelector('.close-outside');
    if (outside) outside.addEventListener('click', close);
    const closeButton = overlay.querySelector('aside > header .close');
    if (closeButton) closeButton.addEventListener('click', close);
  }

  if (toggle) toggle.addEventListener('click', () => open('mobile'));

  // SearchToggle (header) + the menu drawer's first item
  for (const button of document.querySelectorAll('.header-ctas > button, .header-menu-mobile > button')) {
    button.addEventListener('click', () => open('search'));
  }

  // HeaderMenu's NavLinks carry onClick={close} (desktop and drawer)
  for (const link of document.querySelectorAll('.header-menu-desktop a, .header-menu-mobile a')) {
    link.addEventListener('click', close);
  }

  // CartBadge: <a href="/cart" onClick={e.preventDefault(); open('cart')}>
  const badge = header ? header.querySelector('.header-ctas a[href="/cart"]') : null;
  if (badge) {
    badge.addEventListener('click', (event) => {
      event.preventDefault();
      open('cart');
    });
  }

  /* ---- useScrolled(8) --------------------------------------------------- */
  if (header) {
    const onScroll = () => header.setAttribute('data-scrolled', window.scrollY > 8 ? 'true' : 'false');
    onScroll();
    window.addEventListener('scroll', onScroll, {passive: true});
  }

  /* ---- cart drawer ------------------------------------------------------ */
  const cartOverlay = overlays.cart;
  if (!cartOverlay) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let queue = Promise.resolve();
  let outstanding = 0; // drawer changes submitted and not yet answered
  const itemsLabel = (n) => `${n} ${n === 1 ? 'item' : 'items'}`;

  // CartEmpty's action and CartLineItem's title link close the drawer; Checkout does not.
  cartOverlay.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || !cartOverlay.contains(link)) return;
    if (link.closest('.cart-summary-aside')) return;
    if (link.closest('aside > main')) close();
  });

  function focusKey(el) {
    if (!el || !cartOverlay.contains(el) || el === document.body) return null;
    if (el.id) return {id: el.id};
    const form = el.closest('form');
    const lineInput = form ? form.querySelector('input[name="id"]') : null;
    return {
      tag: el.tagName,
      name: el.getAttribute('name') || '',
      label: el.getAttribute('aria-label') || '',
      line: lineInput ? lineInput.value : '',
      text: el.textContent,
    };
  }

  function findByKey(key) {
    if (!key) return null;
    if (key.id) return document.getElementById(key.id);
    for (const el of cartOverlay.querySelectorAll(key.tag)) {
      const form = el.closest('form');
      const lineInput = form ? form.querySelector('input[name="id"]') : null;
      if (
        (el.getAttribute('name') || '') === key.name &&
        (el.getAttribute('aria-label') || '') === key.label &&
        (lineInput ? lineInput.value : '') === key.line &&
        el.textContent === key.text
      )
        return el;
    }
    return null;
  }

  /* Swap in the freshly rendered drawer (count stamp + <main>) and header count. */
  function applySection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('.overlay[data-aside="cart"]');
    if (next) {
      const key = focusKey(document.activeElement);
      const scroller = cartOverlay.querySelector('.cart-main');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      // The discount field is uncontrolled on live: its text survives the update.
      const discountInput = cartOverlay.querySelector('input[name="discountCode"]');
      const discountValue = discountInput ? discountInput.value : '';

      const headRow = cartOverlay.querySelector('aside > header > div');
      const nextHeadRow = next.querySelector('aside > header > div');
      if (headRow && nextHeadRow) headRow.replaceWith(nextHeadRow);
      const main = cartOverlay.querySelector('aside > main');
      const nextMain = next.querySelector('aside > main');
      if (main && nextMain) main.replaceWith(nextMain);

      const nextScroller = cartOverlay.querySelector('.cart-main');
      if (nextScroller) nextScroller.scrollTop = scrollTop;
      const nextDiscountInput = cartOverlay.querySelector('input[name="discountCode"]');
      if (nextDiscountInput && discountValue) nextDiscountInput.value = discountValue;
      const target = findByKey(key);
      if (target && typeof target.focus === 'function') target.focus();
    }
    const nextBadge = doc.querySelector('.header-ctas a[href="/cart"]');
    if (badge && nextBadge) badge.innerHTML = nextBadge.innerHTML;
  }

  function refresh() {
    return fetch(`${window.location.pathname}?section_id=header`, {headers: {Accept: 'text/html'}})
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`section ${response.status}`))))
      .then(applySection);
  }

  /* ---- optimistic cart (Hydrogen useOptimisticCart) ----------------------
     Live's drawer changes the quantity, the -/+ values, the "N items" stamp and
     the header "(N)" the moment a button is pressed, and drops a removed line
     at once (the last one swaps in the empty state); prices follow the server.
     The -/+ values must move immediately too, or two quick presses would both
     send the same quantity. */
  const drawerLines = () => Array.from(cartOverlay.querySelectorAll('aside > main li.cart-line'));
  const lineKey = (li) => {
    const input = li.querySelector('input[name="id"]');
    return input ? input.value : '';
  };
  const shownCount = () =>
    drawerLines().reduce((n, li) => {
      const count = li.querySelector('span[aria-live]');
      return n + ((count && Number.parseInt(count.textContent, 10)) || 0);
    }, 0);

  function paintLine(li, quantity) {
    const count = li.querySelector('span[aria-live]');
    if (count) count.textContent = String(quantity);
    const prev = Math.max(0, quantity - 1);
    const next = quantity + 1;
    const dec = li.querySelector('button[name="decrease-quantity"]');
    const inc = li.querySelector('button[name="increase-quantity"]');
    if (dec) {
      dec.value = String(prev);
      dec.disabled = quantity <= 1;
      const input = dec.form && dec.form.querySelector('input[name="quantity"]');
      if (input) input.value = String(prev);
    }
    if (inc) {
      inc.value = String(next);
      const input = inc.form && inc.form.querySelector('input[name="quantity"]');
      if (input) input.value = String(next);
    }
  }

  /* Header.tsx CartBadge "(N)" and PageLayout.tsx CartCountStamp, from a count. */
  function paintCounts(count) {
    if (badge) {
      let span = badge.querySelector('span.tabular-nums');
      if (count > 0) {
        if (!span) {
          span = document.createElement('span');
          span.className = 'tabular-nums';
          badge.appendChild(span);
        }
        span.setAttribute('aria-label', `(items: ${count})`);
        span.textContent = ` (${count})`;
      } else if (span) span.remove();
    }
    const row = cartOverlay.querySelector('aside > header > div');
    if (row) {
      let stamp = row.querySelector(':scope > span');
      if (count > 0) {
        if (!stamp) {
          stamp = document.createElement('span');
          stamp.className = 'text-2xs uppercase text-ink-3 tabular-nums';
          row.appendChild(stamp);
        }
        stamp.textContent = itemsLabel(count);
      } else if (stamp) stamp.remove();
    }
  }

  function optimistic(key, quantity) {
    const li = drawerLines().find((x) => lineKey(x) === key);
    if (!li) return;
    if (quantity > 0) paintLine(li, quantity);
    else {
      li.remove();
      if (!drawerLines().length) {
        const tpl = document.querySelector('template[data-aside-cart-empty]');
        const main = cartOverlay.querySelector('aside > main');
        if (tpl && main) main.replaceChildren(document.importNode(tpl.content, true));
      }
    }
    paintCounts(shownCount());
  }

  function mutate(endpoint, body) {
    return fetch(`${root}${endpoint}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(Object.assign({}, body, {sections: 'header', sections_url: window.location.pathname})),
    }).then((response) => response.json().catch(() => null));
  }

  /* Changes run one at a time; only the answer to the LAST queued change is
     painted (an earlier answer would briefly undo the optimistic state). */
  function enqueue(endpoint, body) {
    outstanding += 1;
    queue = queue
      .then(() => mutate(endpoint, body))
      .catch(() => null)
      .then((cart) => {
        outstanding -= 1;
        if (outstanding > 0) return undefined;
        const html = cart && cart.sections && cart.sections.header;
        const painted = html ? Promise.resolve(applySection(html)) : refresh();
        return painted
          .catch(() => {})
          .then(() => {
            document.dispatchEvent(new CustomEvent('undepleted:cart-updated'));
            document.dispatchEvent(new CustomEvent('cart:updated', {detail: {source: 'aside'}}));
          });
      });
  }

  cartOverlay.addEventListener('submit', (event) => {
    const form = event.target;
    const kind = form && form.getAttribute('data-cart-form');
    if (!kind) return;
    event.preventDefault();
    const data = new FormData(form);
    let endpoint = 'cart/update.js';
    let body;
    if (kind === 'quantity' || kind === 'remove') {
      const submitter = event.submitter;
      const raw = kind === 'remove' ? 0 : submitter && submitter.value !== '' ? submitter.value : data.get('quantity');
      const key = String(data.get('id'));
      const quantity = Math.max(0, Number(raw) || 0);
      endpoint = 'cart/change.js';
      body = {id: key, quantity};
      optimistic(key, quantity);
      // the /cart page (assets/cart-main.js) paints the same change at once
      document.dispatchEvent(new CustomEvent('cart:optimistic', {detail: {source: 'aside', key, quantity}}));
    } else if (kind === 'discount-remove') {
      // DiscountCodesUpdate with discountCodes [] and no new code: removes them all
      body = {discount: ''};
    } else {
      // DiscountCodesUpdate: [the typed code, ...codes already applied]. Gift cards
      // go through the same field (the Ajax Cart API has no gift-card endpoint).
      const field = kind === 'gift-card-apply' ? 'giftCardCode' : 'discountCode';
      const code = String(data.get(field) || '').trim();
      const existing = String(data.get('discountCodes') || '')
        .split(',')
        .filter(Boolean);
      body = {discount: (code ? [code] : []).concat(existing).join(',')};
      if (kind === 'gift-card-apply') {
        const button = form.querySelector('button[type="submit"]');
        if (button) button.disabled = true;
      }
    }
    enqueue(endpoint, body);
  });

  document.addEventListener('undepleted:cart-changed', () => {
    queue = queue.then(refresh).catch(() => {});
  });

  // The /cart page (assets/cart-main.js) changed the cart: re-render the drawer.
  document.addEventListener('cart:updated', (event) => {
    if (event.detail && event.detail.source === 'aside') return;
    queue = queue.then(refresh).catch(() => {});
  });

  // ... and paint its optimistic change at once, as live's single cart does.
  document.addEventListener('cart:optimistic', (event) => {
    const detail = event.detail || {};
    if (detail.source === 'aside' || !detail.key) return;
    optimistic(String(detail.key), Math.max(0, Number(detail.quantity) || 0));
  });

  // Back/forward cache: a restored page shows the cart as it was when the
  // shopper left it (e.g. the PDP from before its Add to cart landed on /cart:
  // header "Cart" and an empty drawer while the cart holds the line). Live keeps
  // one cart for the whole visit, so re-render the drawer and the header count.
  // On /cart, assets/cart-main.js refreshes on the same event and its
  // `cart:updated` re-renders the drawer through the listener above.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || document.querySelector('section[data-cart-main="page"]')) return;
    queue = queue.then(refresh).catch(() => {});
  });
})();
